import { useState } from "react";
import { useAppSelector } from "@app/hooks";
import { selectProjectsFilters } from "@app/features/project/projectSlice";
import { useContextAwareGetAllTags, useContextAwareGetProjects } from "@hooks/index";
import { usePaginate } from "@pages/project/activities/usePaginate";
import { FastProject } from "@typedefs/interfaces";

const ITEMS_PER_PAGE = 15;

interface UseProjectDataReturn {
    tableData: FastProject[];
    isError: boolean;
    isLoading: boolean;
    projectsCount: number;
    handleNext: () => void;
    handlePrev: () => void;
    handlePageClick: (page: number) => void;
    refetchProjects: () => void;
    ITEMS_PER_PAGE: number;
}

// this hook is used to get the projects data
export const useProjectData = (): UseProjectDataReturn => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    const [url, setUrl] = useState<string>(`${baseUrl}/projects/?page=1&page_size=${ITEMS_PER_PAGE}`);

    const { data: tags } = useContextAwareGetAllTags();
    const { showFinalized, tags: tagsIds, showTrashbin } = useAppSelector(selectProjectsFilters);
    const tagNames = tagsIds && tags ? tags?.filter(tag => tagsIds.includes(tag.id)).map(tag => tag.name) : [];

    const {
        data: projectsRes,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useContextAwareGetProjects({ url, fast: true, showFinalized, tags: tagNames, showTrashbin }, {
        refetchOnMountOrArgChange: true,
    });

    const {
        results: projects = [],
        count: projectsCount = 0,
        next,
        previous,
    } = projectsRes || {};

    const { handleNext, handlePrev, handlePageClick } = usePaginate({
        next,
        previous,
        dynamicNext: (page: number) => `/projects/?page=${page}&page_size=${ITEMS_PER_PAGE}`,
        setUrl
    });

    const tableData = isLoading ? Array(2).fill({}) : projects as FastProject[];

    return {
        tableData,
        isError,
        isLoading: isLoading || isFetching,
        projectsCount,
        handleNext,
        handlePrev,
        handlePageClick,
        refetchProjects: refetch,
        ITEMS_PER_PAGE
    };
}; 